import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Building2, Users, TrendingUp, CreditCard, AlertTriangle,
  Search, Filter, Edit3, ExternalLink, Eye, Trash2, Star, Loader2,
  Phone, Crown, Zap, UserPlus, KeyRound, X, Archive, ArchiveRestore, Handshake, BellRing, DollarSign, Copy, Send, Ban, RefreshCw, Loader2 as LoaderIcon, Link2, FlaskConical, Lock, ShieldCheck
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API, STATUS_LABELS, PLAN_LABELS, PLAN_OPTIONS } from './adminConstants';
import { IconLegend } from '@/components/IconLegend';
import AdminRetentionModal from './AdminRetentionModal';
import AdminIncompleteDataModal from './AdminIncompleteDataModal';

const COMPANY_LEGEND = [
  { icon: <Edit3 className="w-3.5 h-3.5" />, cls: 'bg-blue-50 text-blue-600', label: 'Editar dados da empresa' },
  { icon: <Users className="w-3.5 h-3.5" />, cls: 'bg-amber-50 text-amber-600', label: 'Gerenciar usuários e senhas' },
  { icon: <Archive className="w-3.5 h-3.5" />, cls: 'bg-red-50 text-red-600', label: 'Desativar / Reativar empresa' },
  { icon: <Star className="w-3.5 h-3.5" />, cls: 'bg-[#C5A059]/20 text-[#C5A059]', label: 'Marcar como VIP' },
  { icon: <Ban className="w-3.5 h-3.5" />, cls: 'bg-rose-100 text-rose-600', label: 'Isentar de cobrança de CNPJ (sem régua, nunca bloqueia)' },
  { icon: <FlaskConical className="w-3.5 h-3.5" />, cls: 'bg-violet-100 text-violet-700', label: 'Marcar como cadastro de teste (sai dos números do painel, nunca é cobrada)' },
  { icon: <ShieldCheck className="w-3.5 h-3.5" />, cls: 'bg-sky-100 text-sky-700', label: 'Isento de bloqueio: nunca entra no modo Avaliação Retida' },
  { icon: <Lock className="w-3.5 h-3.5" />, cls: 'bg-orange-100 text-orange-700', label: 'Reter agora / Liberar: esconde o conteúdo das avaliações do dono até ele regularizar' },
  { icon: <DollarSign className="w-3.5 h-3.5" />, cls: 'bg-[#C5A059]/20 text-[#C5A059]', label: 'Preço personalizado / desconto' },
  { icon: <Zap className="w-3.5 h-3.5" />, cls: 'bg-emerald-50 text-emerald-600', label: 'Link OK — fechar assinatura em 1 toque' },
  { icon: <Eye className="w-3.5 h-3.5" />, cls: 'bg-purple-50 text-purple-600', label: 'Entrar no painel como o cliente' },
  { icon: <Handshake className="w-3.5 h-3.5" />, cls: 'bg-cyan-100 text-cyan-700', label: 'Vincular / ver parceiro' },
  { icon: <Link2 className="w-3.5 h-3.5" />, cls: 'bg-indigo-50 text-indigo-600', label: 'Vincular assinatura existente do Asaas (reativa pagante)' },
  { icon: <BellRing className="w-3.5 h-3.5" />, cls: 'bg-orange-50 text-orange-600', label: 'Reativar promoções (cliente desinscrito)' },
  { icon: <ExternalLink className="w-3.5 h-3.5" />, cls: 'bg-emerald-50 text-emerald-600', label: 'Abrir página de avaliação da empresa' },
];
import { PLAN_CATALOG } from '@/constants/plans';

const BILLING_META = {
  em_dia: { label: 'Em dia', cls: 'bg-emerald-100 text-emerald-700' },
  a_vencer: { label: 'A vencer', cls: 'bg-amber-100 text-amber-700' },
  em_atraso: { label: 'Em atraso', cls: 'bg-red-100 text-red-700' },
  sem_cobranca: { label: 'Sem cobrança', cls: 'bg-fuchsia-100 text-fuchsia-700' },
  vinculo_perdido: { label: 'Vínculo perdido', cls: 'bg-slate-800 text-white' },
  cortesia: { label: 'Cortesia/Isento', cls: 'bg-blue-100 text-blue-700' },
  free: { label: 'Free', cls: 'bg-slate-100 text-slate-500' },
  arquivada: { label: 'Desativada', cls: 'bg-slate-200 text-slate-500' },
};

const BillingCell = ({ info, companyId, loading }) => {
  if (loading) return <span className="text-slate-300 text-xs">…</span>;
  if (!info) return <span className="text-slate-300 text-xs">-</span>;
  const meta = BILLING_META[info.status] || BILLING_META.free;
  return (
    <div className="flex flex-col items-center gap-0.5" title={info.detail} data-testid={`billing-cell-${companyId}`}>
      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${meta.cls}`} data-testid={`billing-badge-${companyId}`}>{meta.label}</span>
      {info.status === 'em_atraso' && (
        <span className="text-[10px] font-bold text-red-600">{info.days_late}d · R$ {(info.open_value || 0).toFixed(2).replace('.', ',')}</span>
      )}
      {info.status === 'a_vencer' && info.next_due && (
        <span className="text-[10px] text-amber-600">vence {info.next_due.split('-').reverse().join('/')}</span>
      )}
      {info.invoice_url && (
        <a href={info.invoice_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-600 underline" data-testid={`billing-invoice-${companyId}`}>ver boleto</a>
      )}
    </div>
  );
};

const AdminCompaniesTab = () => {
  const { getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reviewsPeriod, setReviewsPeriod] = useState('total');
  const [reviewsFilter, setReviewsFilter] = useState('all');
  const [sortMode, setSortMode] = useState('recent');
  const [affiliateFilter, setAffiliateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editInfoCompany, setEditInfoCompany] = useState(null);
  const [infoError, setInfoError] = useState(null);
  const [editInfoData, setEditInfoData] = useState({ company_name: '', owner_name: '', owner_email: '', owner_whatsapp: '', slug: '', referred_by: '', plan: '', vip_expires_at: '' });
  const [savingInfo, setSavingInfo] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  const [retryingAsaas, setRetryingAsaas] = useState(null);
  const [linkingAsaas, setLinkingAsaas] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [testFilter, setTestFilter] = useState('real');
  const [testCandidates, setTestCandidates] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [applyingTest, setApplyingTest] = useState(false);
  const [showRetention, setShowRetention] = useState(false);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [billing, setBilling] = useState(null);
  const [billingFilter, setBillingFilter] = useState('all');
  const [refreshingBilling, setRefreshingBilling] = useState(false);
  const [dunning, setDunning] = useState(false);

  // ─── Link Partner state ────────────────────────────────────────────────────
  const [partnersList, setPartnersList] = useState([]);
  const [linkPartnerCompany, setLinkPartnerCompany] = useState(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [savingLinkPartner, setSavingLinkPartner] = useState(false);

  const openLinkPartnerModal = async (company) => {
    setLinkPartnerCompany(company);
    setSelectedPartnerId(company.partner_id || '');
    if (partnersList.length === 0) {
      try {
        const token = getToken();
        const res = await axios.get(`${API}/admin/partners`, { headers: { Authorization: `Bearer ${token}` } });
        setPartnersList((res.data || []).filter(p => p.status === 'active'));
      } catch {
        toast.error('Erro ao carregar parceiros');
      }
    }
  };

  const handleSaveLinkPartner = async () => {
    if (!linkPartnerCompany) return;
    setSavingLinkPartner(true);
    try {
      const token = getToken();
      await axios.post(
        `${API}/admin/companies/${linkPartnerCompany.id}/link-partner`,
        { partner_id: selectedPartnerId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(selectedPartnerId ? 'Empresa vinculada ao parceiro!' : 'Vínculo removido.');
      setLinkPartnerCompany(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao vincular parceiro');
    } finally {
      setSavingLinkPartner(false);
    }
  };

  // ─── OK Link Manual state ──────────────────────────────────────────────────
  const [okCompany, setOkCompany] = useState(null);
  const [okData, setOkData] = useState(null);
  const [okSending, setOkSending] = useState(false);

  const openOkModal = async (company) => {
    setOkCompany(company); setOkData(null);
    try {
      const r = await axios.post(`${API}/admin/company/${company.id}/ok-link`, {},
        { headers: { Authorization: `Bearer ${getToken()}` } });
      setOkData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar link OK');
      setOkCompany(null);
    }
  };

  const sendOkWhatsapp = async () => {
    setOkSending(true);
    try {
      await axios.post(`${API}/admin/company/${okCompany.id}/ok-link`, { send_whatsapp: true },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Link OK enviado no WhatsApp de ${okCompany.name}`);
      setOkCompany(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar');
    } finally { setOkSending(false); }
  };

  // ─── Custom Price state ────────────────────────────────────────────────────
  const [priceCompany, setPriceCompany] = useState(null);
  const [priceValue, setPriceValue] = useState('');
  const [pricePct, setPricePct] = useState('');
  const [priceReason, setPriceReason] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  const fullPriceOf = (c) => {
    if (c?.recurring_discount_full_price) return c.recurring_discount_full_price;
    return PLAN_CATALOG.find(p => p.id === c?.plan)?.price || 0;
  };

  const openPriceModal = (company) => {
    setPriceCompany(company);
    setPriceValue(company.custom_price != null ? String(company.custom_price.toFixed(2)) : '');
    setPricePct('');
    setPriceReason(company.recurring_discount_reason || '');
  };

  const submitCustomPrice = async (payload) => {
    setSavingPrice(true);
    try {
      const r = await axios.put(
        `${API}/admin/company/${priceCompany.id}/custom-price`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const d = r.data;
      toast.success(
        payload.reset
          ? `Preço cheio restaurado (R$ ${d.new_value.toFixed(2).replace('.', ',')})`
          : `Preço atualizado: R$ ${d.new_value.toFixed(2).replace('.', ',')}/mês${d.asaas_updated ? ' — já valendo na próxima fatura' : ''}`
      );
      if (d.note) toast.info(d.note);
      setPriceCompany(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar preço');
    } finally { setSavingPrice(false); }
  };

  const handleSavePrice = () => {
    const v = parseFloat((priceValue || '').replace(',', '.'));
    const p = parseFloat((pricePct || '').replace(',', '.'));
    if (v > 0) return submitCustomPrice({ value: v, reason: priceReason });
    if (p > 0) return submitCustomPrice({ discount_pct: p, reason: priceReason });
    toast.error('Informe o valor em R$ ou o desconto em %');
  };

  // ─── Multi-User Manager state ──────────────────────────────────────────────
  const [usersCompany, setUsersCompany] = useState(null); // company sendo gerenciada
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '' });
  const [savingNewUser, setSavingNewUser] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const openUsersModal = async (company) => {
    setUsersCompany(company);
    setUsersList([]);
    setNewUserForm({ name: '', email: '', password: '' });
    setUsersLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/admin/companies/${company.id}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsersList(res.data.users || []);
    } catch {
      toast.error('Erro ao carregar usuarios da empresa');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUserForm.name.trim() || !newUserForm.email.trim() || !newUserForm.password.trim()) {
      toast.error('Preencha nome, email e senha');
      return;
    }
    if (newUserForm.password.length < 6) {
      toast.error('Senha precisa ter no minimo 6 caracteres');
      return;
    }
    setSavingNewUser(true);
    try {
      const token = getToken();
      const res = await axios.post(
        `${API}/admin/companies/${usersCompany.id}/users`,
        newUserForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setNewUserForm({ name: '', email: '', password: '' });
      // Recarrega a lista
      const usersRes = await axios.get(`${API}/admin/companies/${usersCompany.id}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsersList(usersRes.data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar usuario');
    } finally {
      setSavingNewUser(false);
    }
  };

  const handleResetPassword = async () => {
    if (resetPassword.length < 6) {
      toast.error('Senha precisa ter no minimo 6 caracteres');
      return;
    }
    setResetting(true);
    try {
      const token = getToken();
      const res = await axios.post(
        `${API}/admin/users/${resetUserId}/reset-password`,
        { password: resetPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setResetUserId(null);
      setResetPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao resetar senha');
    } finally {
      setResetting(false);
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (!window.confirm(`Remover acesso de ${userEmail} a esta empresa?\n\nEsta acao nao pode ser desfeita.`)) return;
    try {
      const token = getToken();
      const res = await axios.delete(
        `${API}/admin/companies/${usersCompany.id}/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      const usersRes = await axios.get(`${API}/admin/companies/${usersCompany.id}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsersList(usersRes.data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover acesso');
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  const handleReactivatePromo = async (company) => {
    if (!window.confirm(`Reativar comunicações promocionais para "${company.name}"?\n\nA empresa voltará a receber e-mails e WhatsApps de oferta automáticos.`)) return;
    try {
      const token = getToken();
      const res = await axios.post(`${API}/admin/companies/${company.id}/reactivate-promo`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao reativar promoções');
    }
  };

  const handleArchive = async (company) => {
    const isArchived = !!company.archived;
    const action = isArchived ? 'reativar' : 'arquivar (desativar)';
    if (!window.confirm(`Tem certeza que deseja ${action} "${company.name}"?\n\n${isArchived ? 'A empresa volta a funcionar normalmente.' : 'Os usuarios desta empresa nao conseguirao mais logar e o formulario publico ficara indisponivel. Os dados sao preservados e podem ser reativados depois.'}`)) return;
    try {
      const token = getToken();
      const path = isArchived ? 'unarchive' : 'archive';
      const res = await axios.post(`${API}/admin/companies/${company.id}/${path}`, isArchived ? {} : { reason: 'Desativada pelo admin' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const handleRetryAsaas = async (company) => {
    if (!window.confirm(`Gerar cobranca Asaas para "${company.name}"?\n\nIsso vai criar customer + assinatura e enviar o link de pagamento para ${company.owner_email || 'o cadastro'}.`)) return;
    setRetryingAsaas(company.id);
    try {
      const res = await axios.post(
        `${API}/admin/companies/${company.id}/retry-asaas`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const url = res.data?.invoice_url;
      if (url) {
        toast.success(`Cobranca criada! Link enviado para ${res.data.email_sent_to}.`, { duration: 6000 });
        // Oferecer copiar o link rapidamente
        try {
          await navigator.clipboard.writeText(url);
          toast.info('Link de pagamento tambem foi copiado para a area de transferencia.');
        } catch { /* ok */ }
      } else {
        toast.success('Cobranca criada no Asaas. Link chegara em instantes.');
      }
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erro ao gerar cobranca';
      toast.error(msg);
    } finally {
      setRetryingAsaas(null);
    }
  };

  const handleDunning = async () => {
    const atrasados = Object.entries(billing?.companies || {})
      .filter(([, v]) => v.status === 'em_atraso').map(([id]) => id);
    if (!atrasados.length) { toast.info('Nenhuma empresa com boleto vencido.'); return; }
    if (!window.confirm(`Enviar o aviso de atraso no WhatsApp para ${atrasados.length} cliente(s) com boleto vencido?\n\nA mensagem vai com o link do boleto e tom leve. Nada e enviado para quem esta em dia.`)) return;
    setDunning(true);
    try {
      const res = await axios.post(`${API}/admin/billing/overdue-reminders`,
        { company_ids: atrasados }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`${res.data.total_sent} aviso(s) enviado(s) no WhatsApp.`, { duration: 7000 });
      (res.data.skipped || []).forEach(s => toast.warning(`${s.company}: ${s.motivo}`));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar os avisos de atraso');
    } finally {
      setDunning(false);
    }
  };

  const fetchBilling = useCallback(async (refresh = false) => {
    if (refresh) setRefreshingBilling(true);
    try {
      const res = await axios.get(`${API}/admin/billing-status${refresh ? '?refresh=true' : ''}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      setBilling(res.data);
      if (refresh) toast.success('Status de cobranca atualizado a partir do Asaas.');
    } catch (e) {
      if (refresh) toast.error(e?.response?.data?.detail || 'Erro ao consultar o Asaas');
    } finally {
      setRefreshingBilling(false);
    }
  }, [getToken]);

  const handleLinkAsaas = async (company) => {
    if (!window.confirm(`Vincular "${company.name}" a uma assinatura JA existente no Asaas?\n\nUse quando o cliente paga no Asaas mas o app perdeu o vinculo (caiu para Free/pagamento expirado). Nenhuma cobranca nova e criada.`)) return;
    setLinkingAsaas(company.id);
    try {
      const res = await axios.post(
        `${API}/admin/company/${company.id}/link-asaas`, {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const abertas = res.data?.open_payments?.length || 0;
      toast.success(`${res.data.message}${abertas ? ` ${abertas} cobranca(s) em aberto.` : ''}`, { duration: 8000 });
      await fetchData();
      await fetchBilling(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao vincular assinatura do Asaas');
    } finally {
      setLinkingAsaas(null);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const h = { Authorization: `Bearer ${token}` };
      const [statsRes, companiesRes, affRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: h }),
        axios.get(`${API}/admin/companies`, { headers: h }),
        axios.get(`${API}/admin/affiliates`, { headers: h }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setAffiliates(Array.isArray(affRes.data) ? affRes.data : affRes.data.affiliates || []);
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchBilling(false); }, [fetchBilling]);

  const defaultFirstDue = (company) => {
    const hoje = new Date();
    let venc = company.created_at ? new Date(company.created_at) : null;
    if (venc && !isNaN(venc)) venc.setDate(venc.getDate() + 30);
    if (!venc || isNaN(venc) || venc <= hoje) {
      venc = new Date();
      venc.setDate(venc.getDate() + 3);
    }
    return venc.toISOString().slice(0, 10);
  };

  const openEditInfo = (company) => {
    setEditInfoCompany(company);
    setInfoError(null);
    setEditInfoData({
      company_name: company.name || '',
      owner_name: company.owner_name || '',
      owner_email: company.owner_email || '',
      owner_whatsapp: company.owner_whatsapp || '',
      slug: company.slug || '',
      referred_by: company.referred_by || '',
      plan: company.plan || 'free',
      vip_expires_at: company.vip_expires_at ? company.vip_expires_at.slice(0, 10) : '',
      document: (company.document || '').replace(/\D/g, ''),
      first_due_date: defaultFirstDue(company)
    });
  };

  const saveEditInfo = async () => {
    if (!editInfoCompany) return;
    setSavingInfo(true);
    setInfoError(null);
    try {
      const token = getToken();
      const res = await axios.put(`${API}/admin/company/${editInfoCompany.id}/info`, editInfoData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const erro = res.data?.error || res.data?.warning;
      if (erro) {
        setInfoError(erro);
        toast.error(erro, { duration: 12000 });
        fetchData();
        fetchBilling(true);
        return;
      }
      const a = res.data?.asaas;
      if (a?.created) {
        toast.success(`Assinatura criada no Asaas · 1º vencimento ${a.first_due_date.split('-').reverse().join('/')}`
          + (a.whatsapp_sent ? ' · WhatsApp enviado' : ''), { duration: 9000 });
      } else {
        toast.success(res.data?.owner_relinked ? res.data.message : 'Empresa atualizada!',
          res.data?.owner_relinked ? { duration: 9000 } : undefined);
      }
      setEditInfoCompany(null);
      fetchData();
      if (a) fetchBilling(true);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao salvar';
      setInfoError(typeof msg === 'string' ? msg : 'Erro ao salvar');
      toast.error(typeof msg === 'string' ? msg : 'Erro ao salvar');
    }
    finally { setSavingInfo(false); }
  };

  const toggleVip = async (companyId, currentVip) => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/company/${companyId}/vip`, { is_vip: !currentVip }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentVip ? 'VIP removido' : 'VIP ativado');
      fetchData();
    } catch { toast.error('Erro ao alterar VIP'); }
  };

  const [syncingAsaas, setSyncingAsaas] = useState(false);

  const syncAsaas = async () => {
    setSyncingAsaas(true);
    try {
      const h = { headers: { Authorization: `Bearer ${getToken()}` } };
      const prev = await axios.post(`${API}/admin/asaas/sync-customers`, { dry_run: true }, h);
      const d = prev.data;
      const msg = `Corrigir telefone/e-mail de ${d.atualizados} cliente(s) no Asaas?\n\n`
        + `Total com cadastro no Asaas: ${d.total_clientes}\n`
        + `Sem e-mail real (ficam só com telefone): ${d.sem_email_real}`;
      if (!window.confirm(msg)) { setSyncingAsaas(false); return; }
      const r = await axios.post(`${API}/admin/asaas/sync-customers`, {}, h);
      toast.success(`Asaas atualizado: ${r.data.atualizados} cliente(s)${r.data.falhas ? ` · ${r.data.falhas} falha(s)` : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao sincronizar com o Asaas'); }
    finally { setSyncingAsaas(false); }
  };

  const toggleBillingExempt = async (companyId, current) => {
    try {
      await axios.put(`${API}/admin/company/${companyId}/billing-exempt`, { billing_exempt: !current }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(current ? 'Cobrança reativada para esta empresa' : 'Empresa isenta de cobrança');
      fetchData();
    } catch { toast.error('Erro ao alterar isenção'); }
  };

  const toggleTestFlag = async (company) => {
    const marking = !company.is_test;
    if (marking && !window.confirm(
      `Marcar "${company.name}" como cadastro de teste?\n\n`
      + '• Sai da contagem de clientes, MRR e CAC do painel\n'
      + '• Nunca recebe cobrança, boleto nem lembrete\n'
      + '• Nada é apagado — você pode desmarcar depois'
    )) return;
    try {
      await axios.put(`${API}/admin/company/${company.id}/test-flag`, { is_test: marking }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(marking ? 'Marcada como teste — fora dos números do painel' : 'Empresa voltou a contar como cliente real');
      fetchData();
    } catch { toast.error('Erro ao alterar marcação de teste'); }
  };

  const toggleRetainNow = async (company) => {
    const retendo = !company.retain_now_at;
    if (retendo && !window.confirm(
      `Reter as avaliações de "${company.name}" agora?\n\n`
      + '• O conteúdo das avaliações fica oculto para o dono até ele regularizar\n'
      + '• O cliente final não vê nada diferente\n'
      + '• Pagamento confirmado (ou "Liberar") devolve tudo na hora'
    )) return;
    try {
      const url = `${API}/admin/companies/${company.id}/retain-now`;
      const headers = { Authorization: `Bearer ${getToken()}` };
      if (retendo) await axios.post(url, {}, { headers });
      else await axios.delete(url, { headers });
      toast.success(retendo ? 'Empresa retida manualmente' : 'Retenção manual liberada');
      fetchData();
    } catch { toast.error('Erro ao alterar a retenção manual'); }
  };

  const toggleBlockExempt = async (company) => {
    try {
      const res = await axios.put(`${API}/admin/company/${company.id}/block-exempt`, { block_exempt: !company.block_exempt }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(res.data.block_exempt
        ? 'Isento de bloqueio — nunca entra no modo Avaliação Retida'
        : 'Voltou a seguir a regra de Avaliação Retida');
      fetchData();
    } catch { toast.error('Erro ao alterar isenção de bloqueio'); }
  };

  const openTestCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const res = await axios.get(`${API}/admin/test-candidates`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setTestCandidates(res.data);
      setSelectedCandidates((res.data.candidates || []).map(c => c.id));
    } catch { toast.error('Erro ao detectar cadastros de teste'); }
    finally { setLoadingCandidates(false); }
  };

  const applyTestCandidates = async () => {
    if (!selectedCandidates.length) { toast.error('Selecione ao menos uma empresa'); return; }
    setApplyingTest(true);
    try {
      const res = await axios.post(`${API}/admin/test-candidates/apply`, {
        company_ids: selectedCandidates, is_test: true
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`${res.data.updated} empresa(s) marcada(s) como teste`);
      setTestCandidates(null);
      setSelectedCandidates([]);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao marcar empresas'); }
    finally { setApplyingTest(false); }
  };

  const handleImpersonate = async (companyId) => {
    try {
      const token = getToken();
      const res = await axios.post(`${API}/admin/impersonate`, { type: 'owner', id: companyId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.setItem('token', res.data.token);
      window.open(res.data.redirect || '/dashboard', '_blank');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao personificar'); }
  };

  const testWhatsApp = async (companyId) => {
    setTestingWhatsApp(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/admin/test-whatsapp/${companyId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Mensagem de teste enviada!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao testar WhatsApp'); }
    finally { setTestingWhatsApp(false); }
  };

  const filtered = companies.filter(c => {
    // Por padrao, esconde empresas desativadas. Toggle "Mostrar desativadas" reverte.
    if (!showArchived && c.archived) return false;
    if (showArchived && !c.archived) return false;
    if (testFilter === 'real' && c.is_test) return false;
    if (testFilter === 'test' && !c.is_test) return false;
    if (planFilter !== 'all' && c.plan !== planFilter) return false;
    const periodCount = reviewsPeriod === 'total' ? (c.total_feedbacks || 0)
      : reviewsPeriod === '7d' ? (c.feedbacks_7d || 0)
      : reviewsPeriod === '30d' ? (c.feedbacks_30d || 0)
      : (c.feedbacks_90d || 0);
    if (reviewsFilter === 'zero' && periodCount !== 0) return false;
    if (reviewsFilter === '1plus' && periodCount < 1) return false;
    if (reviewsFilter === '5plus' && periodCount < 5) return false;
    if (reviewsFilter === '10plus' && periodCount < 10) return false;
    if (statusFilter !== 'all') {
      const s = c.subscription_status || (c.plan === 'free' ? 'none' : 'active');
      if (s !== statusFilter) return false;
    }
    if (billingFilter !== 'all') {
      if ((billing?.companies?.[c.id]?.status || 'free') !== billingFilter) return false;
    }
    if (affiliateFilter !== 'all') {
      if (affiliateFilter === 'organic' && c.referred_by) return false;
      if (affiliateFilter !== 'organic' && c.referred_by !== affiliateFilter) return false;
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (c.name?.toLowerCase().includes(term) || c.owner_name?.toLowerCase().includes(term) || c.owner_email?.toLowerCase().includes(term) || c.slug?.toLowerCase().includes(term));
    }
    return true;
  });

  const periodCountOf = (c) => reviewsPeriod === 'total' ? (c.total_feedbacks || 0)
    : reviewsPeriod === '7d' ? (c.feedbacks_7d || 0)
    : reviewsPeriod === '30d' ? (c.feedbacks_30d || 0)
    : (c.feedbacks_90d || 0);

  let abcMap = {};
  let displayList = filtered;
  if (sortMode === 'abc' || sortMode === 'reviews_desc') {
    displayList = [...filtered].sort((a, b) => periodCountOf(b) - periodCountOf(a));
    const total = displayList.reduce((s, c) => s + periodCountOf(c), 0);
    let cum = 0;
    displayList.forEach(c => {
      cum += periodCountOf(c);
      abcMap[c.id] = total === 0 || periodCountOf(c) === 0 ? 'C' : (cum / total <= 0.8 ? 'A' : cum / total <= 0.95 ? 'B' : 'C');
    });
  } else if (sortMode === 'reviews_asc') {
    displayList = [...filtered].sort((a, b) => periodCountOf(a) - periodCountOf(b));
  } else if (sortMode === 'name') {
    displayList = [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  }

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin w-10 h-10 border-4 border-[#C5A059] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="empresas-section">
      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border shadow-sm" data-testid="admin-kpi-companies"><CardContent className="pt-5 pb-5"><div className="flex items-start justify-between"><div><p className="text-sm text-slate-500">Empresas</p><p className="text-3xl font-bold text-[#0A2647]">{stats.total_companies}</p>{stats.test_companies_count > 0 && <p className="text-xs text-violet-600 mt-1" data-testid="kpi-test-excluded">{stats.test_companies_count} cadastro(s) de teste fora da conta</p>}</div><div className="w-10 h-10 bg-[#0A2647]/10 rounded-xl flex items-center justify-center"><Building2 className="w-5 h-5 text-[#0A2647]" /></div></div></CardContent></Card>
          <Card className="border shadow-sm" data-testid="admin-kpi-users"><CardContent className="pt-5 pb-5"><div className="flex items-start justify-between"><div><p className="text-sm text-slate-500">Usuarios</p><p className="text-3xl font-bold text-[#0A2647]">{stats.total_users}</p></div><div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div></div></CardContent></Card>
          <Card className="border shadow-sm" data-testid="admin-kpi-feedbacks"><CardContent className="pt-5 pb-5"><div className="flex items-start justify-between"><div><p className="text-sm text-slate-500">Avaliações</p><p className="text-3xl font-bold text-[#C5A059]">{stats.total_feedbacks}</p></div><div className="w-10 h-10 bg-[#C5A059]/10 rounded-xl flex items-center justify-center"><TrendingUp className="w-5 h-5 text-[#C5A059]" /></div></div></CardContent></Card>
          <Card className="border shadow-sm" data-testid="admin-kpi-revenue"><CardContent className="pt-5 pb-5"><div className="flex items-start justify-between"><div><p className="text-sm text-slate-500">Receita/Mes</p><p className="text-3xl font-bold text-emerald-600">R$ {stats.monthly_revenue.toFixed(2).replace('.', ',')}</p><p className="text-xs text-slate-400 mt-1">{stats.active_paid_count} assinantes ativos</p></div><div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center"><CreditCard className="w-5 h-5 text-emerald-600" /></div></div></CardContent></Card>
        </div>
      )}

      {/* Empresas pendentes Asaas */}
      {(() => {
        const pendingAsaas = companies.filter(c => c.subscription_status === 'pending_asaas');
        if (pendingAsaas.length === 0) return null;
        return (
          <Card className="border-0 shadow-sm border-l-4 border-l-red-500 bg-red-50/50" data-testid="pending-asaas-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <p className="font-bold text-sm text-red-800">{pendingAsaas.length} empresa(s) sem cobranca Asaas</p>
                <span className="text-[10px] text-red-500 ml-auto">Cadastradas via checkout quando Asaas estava indisponivel</span>
              </div>
              <div className="space-y-2">
                {pendingAsaas.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200" data-testid={`pending-asaas-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.owner_email || c.owner_name || '-'} | Plano: {PLAN_LABELS[c.plan] || c.plan}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">Cadastrada em: {c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : '-'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700">Sem Cobranca</span>
                      <Button
                        size="sm"
                        className="h-8 text-xs bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-bold"
                        onClick={() => handleRetryAsaas(c)}
                        disabled={retryingAsaas === c.id}
                        data-testid={`retry-asaas-${c.id}`}
                      >
                        {retryingAsaas === c.id ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Gerando...</>
                        ) : (
                          <><Zap className="w-3 h-3 mr-1" />Gerar Cobranca</>
                        )}
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300" onClick={() => openEditInfo(c)} data-testid={`edit-pending-${c.id}`}>
                        <Edit3 className="w-3 h-3 mr-1" />Editar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-red-400 mt-3">Clique em "Gerar Cobranca" para criar automaticamente no Asaas e enviar o link por email ao cliente.</p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Plan + Status Distribution */}
      {stats && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card className="border shadow-sm"><CardHeader className="pb-3"><CardTitle className="text-base font-semibold flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#C5A059]" />Distribuicao por Plano</CardTitle></CardHeader><CardContent><div className="space-y-3">{Object.entries(stats.plan_distribution).map(([plan, count]) => (<div key={plan} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"><span className="text-sm font-medium text-slate-700">{PLAN_LABELS[plan] || plan}</span><span className="text-sm font-bold text-[#0A2647] bg-slate-100 px-3 py-1 rounded-full">{count}</span></div>))}</div></CardContent></Card>
          <Card className="border shadow-sm"><CardHeader className="pb-3"><CardTitle className="text-base font-semibold flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Status de Pagamento</CardTitle></CardHeader><CardContent><div className="space-y-3">{Object.entries(stats.subscription_distribution || {}).map(([status, count]) => { const s = STATUS_LABELS[status] || STATUS_LABELS.none; return (<div key={status} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.color}`}>{s.label}</span><span className="text-sm font-bold text-[#0A2647] bg-slate-100 px-3 py-1 rounded-full">{count}</span></div>); })}</div></CardContent></Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]"><label className="text-xs font-medium text-slate-600 mb-1 block">Buscar</label><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Nome, email, slug..." className="w-full pl-10 pr-3 h-9 border border-slate-300 rounded-lg text-sm" data-testid="search-companies" /></div></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Plano</label><select value={planFilter} onChange={e => setPlanFilter(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-plan"><option value="all">Todos</option>{PLAN_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}</select></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Cobrança</label><select value={billingFilter} onChange={e => setBillingFilter(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-billing"><option value="all">Todas</option>{Object.entries(BILLING_META).map(([k, m]) => <option key={k} value={k}>{m.label}{billing?.counts?.[k] ? ` (${billing.counts[k]})` : ''}</option>)}</select></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Status</label><select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-status"><option value="all">Todos</option><option value="active">Ativo</option><option value="pending">Pendente</option><option value="overdue">Inadimplente</option><option value="none">Gratuito</option></select></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Avaliações</label><select value={reviewsFilter} onChange={e => setReviewsFilter(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-reviews"><option value="all">Todas</option><option value="zero">Nenhuma (0)</option><option value="1plus">1 ou mais</option><option value="5plus">5 ou mais</option><option value="10plus">10 ou mais</option></select></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Período</label><select value={reviewsPeriod} onChange={e => setReviewsPeriod(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-reviews-period"><option value="total">Total</option><option value="7d">Últimos 7 dias</option><option value="30d">Últimos 30 dias</option><option value="90d">Últimos 90 dias</option></select></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Ordem</label><select value={sortMode} onChange={e => setSortMode(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-sort-mode"><option value="recent">Mais recentes</option><option value="reviews_desc">Mais avaliações primeiro</option><option value="reviews_asc">Menos avaliações primeiro</option><option value="abc">Curva ABC (com selo A/B/C)</option><option value="name">Nome (A-Z)</option></select></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Afiliado</label><select value={affiliateFilter} onChange={e => setAffiliateFilter(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-affiliate"><option value="all">Todos</option><option value="organic">Organico</option>{affiliates.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Cadastros de teste</label><select value={testFilter} onChange={e => setTestFilter(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-test"><option value="real">Ocultar testes</option><option value="all">Mostrar todas</option><option value="test">Somente testes</option></select></div>
          </div>
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card className="border shadow-sm overflow-hidden">
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#0A2647]" />
            Empresas ({filtered.length}{showArchived && ' desativadas'})
          </CardTitle>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDunning}
              disabled={dunning || !billing?.counts?.em_atraso}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-40"
              data-testid="dunning-btn"
              title="Enviar o aviso de atraso no WhatsApp (com link do boleto) para quem esta vencido"
            >
              {dunning ? <LoaderIcon className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
              Cobrar atrasados{billing?.counts?.em_atraso ? ` (${billing.counts.em_atraso})` : ''}
            </button>
            <button
              onClick={() => fetchBilling(true)}
              disabled={refreshingBilling}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 disabled:opacity-60"
              data-testid="refresh-billing-btn"
              title={billing?.generated_at ? `Status de cobranca gerado em ${new Date(billing.generated_at).toLocaleString('pt-BR')}` : 'Consultar o Asaas agora'}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshingBilling ? 'animate-spin' : ''}`} />
              Atualizar cobrança
            </button>
            <button
              onClick={syncAsaas}
              disabled={syncingAsaas}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-60"
              data-testid="sync-asaas-btn"
              title="Corrige telefone e e-mail dos clientes já criados no Asaas"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncingAsaas ? 'animate-spin' : ''}`} />
              Sincronizar Asaas
            </button>
            <button
              onClick={() => setShowIncomplete(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-100 text-orange-700 hover:bg-orange-200"
              data-testid="incomplete-open-btn"
              title="Empresas com e-mail, CNPJ, logo, Google, cidade ou WhatsApp faltando"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Dados faltando
            </button>
            <button
              onClick={() => setShowRetention(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200"
              data-testid="retention-open-btn"
              title="Modo Avaliação Retida: liga/desliga e escolhe quem fica isento"
            >
              <Lock className="w-3.5 h-3.5" />
              Avaliação Retida
            </button>
            <button
              onClick={openTestCandidates}
              disabled={loadingCandidates}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-violet-100 text-violet-700 hover:bg-violet-200 disabled:opacity-60"
              data-testid="detect-test-btn"
              title="Encontra cadastros que parecem ser de teste/demonstração e marca de uma vez"
            >
              {loadingCandidates ? <LoaderIcon className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />}
              Detectar testes
            </button>
            <button
              onClick={() => setShowArchived(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${showArchived ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="toggle-show-archived"
            >
              <Archive className="w-3.5 h-3.5" />
              {showArchived ? 'Voltar para ativas' : 'Mostrar desativadas'}
            </button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="px-4 pt-3 pb-1"><IconLegend items={COMPANY_LEGEND} /></div>
          {(billing?.counts?.vinculo_perdido || billing?.orphans?.length) ? (
            <div className="mx-4 mb-2 rounded-lg border border-slate-800 bg-slate-900 text-white px-4 py-3" data-testid="broken-link-banner">
              <p className="text-sm font-bold">🔗 {(billing.counts?.vinculo_perdido || 0) + (billing.orphans?.length || 0)} caso(s) de vínculo perdido com o Asaas</p>
              <p className="text-xs text-slate-300 mt-1">
                Clientes que pagam (ou têm assinatura ativa) mas o app não reconhece. Filtre por <strong>Cobrança → Vínculo perdido</strong> e use o botão 🔗 em cada empresa.
                {billing.orphans?.length ? ` ${billing.orphans.length} assinatura(s) no Asaas sem empresa correspondente: ${billing.orphans.slice(0, 3).map(o => `${o.customer_name} (R$ ${o.value.toFixed(2)})`).join(', ')}.` : ''}
              </p>
            </div>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="companies-table">
              <thead><tr className="bg-slate-50 border-b text-slate-500 text-xs"><th className="text-left px-4 py-3">Empresa</th><th className="text-center px-3 py-3">Plano</th><th className="text-center px-3 py-3">Status</th><th className="text-center px-3 py-3">Cobrança</th><th className="text-center px-3 py-3">Aval.</th><th className="text-center px-3 py-3">Retido</th><th className="text-center px-3 py-3">Afiliado</th><th className="text-center px-3 py-3">Parceiro</th><th className="text-center px-3 py-3">VIP</th><th className="text-center px-3 py-3">Acoes</th></tr></thead>
              <tbody>
                {displayList.map(c => {
                  const status = c.subscription_status || (c.plan === 'free' ? 'none' : 'active');
                  const sl = STATUS_LABELS[status] || STATUS_LABELS.none;
                  return (
                    <tr key={c.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${c.archived ? 'opacity-60 bg-red-50/30' : ''}`} data-testid={`company-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-900 truncate max-w-[200px] flex items-center gap-1.5">
                          {c.name}
                          {c.archived && <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">DESATIVADA</span>}
                          {c.is_test && <span className="text-[9px] font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded" data-testid={`test-badge-${c.id}`}>TESTE</span>}
                        </p>
                        <p className="text-xs text-slate-400">{c.owner_email} | /{c.slug}</p>
                      </td>
                      <td className="text-center px-3 py-3">
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#0A2647]/10 text-[#0A2647]">{PLAN_LABELS[c.plan] || c.plan}</span>
                        {c.custom_price != null && (
                          <p className="text-[10px] font-bold text-[#C5A059] mt-1" data-testid={`custom-price-${c.id}`}>R$ {c.custom_price.toFixed(2).replace('.', ',')}/mês</p>
                        )}
                      </td>
                      <td className="text-center px-3 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${sl.color}`}>{sl.label}</span></td>
                      <td className="text-center px-3 py-3"><BillingCell info={billing?.companies?.[c.id]} companyId={c.id} loading={!billing} /></td>
                      <td className="text-center px-3 py-3 font-bold text-slate-700">
                        {sortMode === 'abc' && (
                          <span className={`inline-block w-5 h-5 leading-5 rounded-full text-[10px] font-extrabold mr-1.5 ${abcMap[c.id] === 'A' ? 'bg-emerald-100 text-emerald-700' : abcMap[c.id] === 'B' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`abc-badge-${c.id}`}>{abcMap[c.id]}</span>
                        )}
                        {reviewsPeriod === 'total' ? (c.total_feedbacks || 0) : (
                          <span>{reviewsPeriod === '7d' ? (c.feedbacks_7d || 0) : reviewsPeriod === '30d' ? (c.feedbacks_30d || 0) : (c.feedbacks_90d || 0)}<span className="text-[10px] font-normal text-slate-400"> /{c.total_feedbacks || 0}</span></span>
                        )}
                      </td>
                      <td className="text-center px-3 py-3" data-testid={`retained-cell-${c.id}`}>
                        {c.retained_count > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold border border-amber-200" title="Avaliações retidas aguardando pagamento">
                            <Lock className="w-3 h-3" />{c.retained_count}
                          </span>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </td>
                      <td className="text-center px-3 py-3 text-xs text-slate-500">{c.referred_by_name || (c.referred_by ? c.referred_by.slice(0, 8) : 'Organico')}</td>
                      <td className="text-center px-3 py-3 text-xs">
                        {c.partner_name ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 font-semibold border border-cyan-200">
                            <Handshake className="w-3 h-3" />{c.partner_name}
                          </span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                      <td className="text-center px-3 py-3">{c.is_vip ? <Crown className="w-4 h-4 text-[#C5A059] mx-auto" /> : <span className="text-slate-300">-</span>}</td>
                      <td className="text-center px-3 py-3">
                        <div className="flex gap-1 justify-center">
                          <button onClick={() => openEditInfo(c)} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Editar" data-testid={`edit-company-${c.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => openUsersModal(c)} className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors" title="Gerenciar usuarios" data-testid={`users-company-${c.id}`}><Users className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleArchive(c)} className={`p-1.5 rounded-lg transition-colors ${c.archived ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' : 'bg-red-50 text-red-600 hover:bg-red-100'}`} title={c.archived ? 'Reativar empresa' : 'Desativar empresa'} data-testid={`archive-company-${c.id}`}>
                            {c.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => toggleVip(c.id, c.is_vip)} className={`p-1.5 rounded-lg transition-colors ${c.is_vip ? 'bg-[#C5A059]/20 text-[#C5A059]' : 'bg-slate-100 text-slate-400'}`} title="Toggle VIP" data-testid={`vip-company-${c.id}`}><Star className="w-3.5 h-3.5" /></button>
                          <button onClick={() => toggleBillingExempt(c.id, c.billing_exempt)} className={`p-1.5 rounded-lg transition-colors ${c.billing_exempt ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-400 hover:bg-rose-50 hover:text-rose-500'}`} title={c.billing_exempt ? 'Isenta de cobrança (sem régua de CNPJ, nunca bloqueia) — clique para reativar' : 'Isentar de cobrança de CNPJ'} data-testid={`billing-exempt-${c.id}`}><Ban className="w-3.5 h-3.5" /></button>
                          <button onClick={() => toggleRetainNow(c)} className={`p-1.5 rounded-lg transition-colors ${c.retain_now_at ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-400 hover:bg-orange-50 hover:text-orange-600'}`} title={c.retain_now_at ? 'Retida manualmente — clique para Liberar' : 'Reter agora: esconde o conteúdo das avaliações até regularizar'} data-testid={`retain-now-${c.id}`}><Lock className="w-3.5 h-3.5" /></button>
                          <button onClick={() => toggleBlockExempt(c)} className={`p-1.5 rounded-lg transition-colors ${c.block_exempt ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-400 hover:bg-sky-50 hover:text-sky-600'}`} title={c.block_exempt ? 'Isento de bloqueio: nunca entra no modo Avaliação Retida — clique para voltar ao normal' : 'Marcar como Isento de bloqueio (nunca retém avaliações)'} data-testid={`block-exempt-${c.id}`}><ShieldCheck className="w-3.5 h-3.5" /></button>
                          <button onClick={() => toggleTestFlag(c)} className={`p-1.5 rounded-lg transition-colors ${c.is_test ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-400 hover:bg-violet-50 hover:text-violet-600'}`} title={c.is_test ? 'Cadastro de teste (fora dos números do painel) — clique para voltar a contar como cliente real' : 'Marcar como cadastro de teste'} data-testid={`test-flag-${c.id}`}><FlaskConical className="w-3.5 h-3.5" /></button>
                          {c.plan !== 'free' && (
                            <button onClick={() => openPriceModal(c)} className={`p-1.5 rounded-lg transition-colors ${c.custom_price != null ? 'bg-[#C5A059]/20 text-[#C5A059]' : 'bg-slate-100 text-slate-500 hover:bg-amber-50 hover:text-[#C5A059]'}`} title="Preço personalizado" data-testid={`custom-price-btn-${c.id}`}><DollarSign className="w-3.5 h-3.5" /></button>
                          )}
                          <button onClick={() => openOkModal(c)} className="p-1.5 rounded-lg transition-colors bg-slate-100 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600" title="Link OK (fechar assinatura em 1 toque)" data-testid={`ok-link-btn-${c.id}`}><Zap className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleImpersonate(c.id)} className="p-1.5 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors" title="Personificar" data-testid={`impersonate-company-${c.id}`}><Eye className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleLinkAsaas(c)} disabled={linkingAsaas === c.id} className="p-1.5 rounded-lg bg-slate-100 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition-colors disabled:opacity-50" title="Vincular assinatura existente do Asaas e reativar pagante" data-testid={`link-asaas-${c.id}`}>{linkingAsaas === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}</button>
                          <button onClick={() => openLinkPartnerModal(c)} className={`p-1.5 rounded-lg transition-colors ${c.partner_id ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-500 hover:bg-cyan-50 hover:text-cyan-600'}`} title={c.partner_id ? 'Vinculada a parceiro' : 'Vincular parceiro'} data-testid={`link-partner-${c.id}`}><Handshake className="w-3.5 h-3.5" /></button>
                          {c.promo_optout && (
                            <button onClick={() => handleReactivatePromo(c)} className="p-1.5 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors" title="Empresa desinscrita de promoções — clique para reativar" data-testid={`reactivate-promo-${c.id}`}><BellRing className="w-3.5 h-3.5" /></button>
                          )}
                          <a href={`/${c.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors" title="Ver formulario"><ExternalLink className="w-3.5 h-3.5" /></a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">Nenhuma empresa encontrada com os filtros atuais.</div>}
        </CardContent>
      </Card>

      {/* Cadastros com dado faltando */}
      {showIncomplete && <AdminIncompleteDataModal onClose={() => setShowIncomplete(false)} />}

      {/* Modo Avaliação Retida */}
      {showRetention && (
        <AdminRetentionModal onClose={() => setShowRetention(false)} onChanged={fetchData} />
      )}

      {/* Detectar cadastros de teste */}
      {testCandidates && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" data-testid="test-candidates-modal">
          <Card className="w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-violet-600" />
                  Cadastros que parecem ser de teste
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Marcar como teste tira a empresa da contagem de clientes, do MRR e do CAC, e nunca cobra.
                  Nada é apagado — é reversível a qualquer momento.
                  {testCandidates.already_flagged ? ` · ${testCandidates.already_flagged} já marcada(s)` : ''}
                </p>
              </div>
              <button onClick={() => setTestCandidates(null)} className="p-1 text-slate-400 hover:text-slate-600" data-testid="close-test-modal"><X className="w-5 h-5" /></button>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1">
              {testCandidates.candidates?.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">Nenhum cadastro suspeito de teste encontrado.</p>
              ) : (
                <div className="space-y-2">
                  {testCandidates.candidates.map(c => (
                    <label key={c.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`candidate-${c.id}`}>
                      <input
                        type="checkbox"
                        checked={selectedCandidates.includes(c.id)}
                        onChange={() => setSelectedCandidates(prev => prev.includes(c.id) ? prev.filter(i => i !== c.id) : [...prev, c.id])}
                        className="mt-1 w-4 h-4 accent-violet-600"
                        data-testid={`candidate-check-${c.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm truncate">{c.name} <span className="text-xs font-normal text-slate-400">· {c.plan_name}</span></p>
                        <p className="text-xs text-slate-500 truncate">{c.owner_email} · {c.total_feedbacks} avaliação(ões)</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {c.reasons.map(r => (
                            <span key={r} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">{r}</span>
                          ))}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </CardContent>
            <div className="border-t p-4 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">{selectedCandidates.length} selecionada(s) de {testCandidates.count}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setTestCandidates(null)} data-testid="cancel-test-apply">Cancelar</Button>
                <Button
                  onClick={applyTestCandidates}
                  disabled={applyingTest || !selectedCandidates.length}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="apply-test-flags"
                >
                  {applyingTest ? <LoaderIcon className="w-4 h-4 animate-spin mr-2" /> : <FlaskConical className="w-4 h-4 mr-2" />}
                  Marcar como teste
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* OK Link Manual Modal */}
      {okCompany && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4" data-testid="ok-link-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#0A2647] flex items-center gap-2"><Zap className="w-5 h-5 text-[#C5A059]" /> Link OK de 1 Toque</h3>
                <p className="text-xs text-slate-500 mt-0.5">{okCompany.name}</p>
              </div>
              <button onClick={() => setOkCompany(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            {!okData ? (
              <div className="flex justify-center py-6"><LoaderIcon className="w-5 h-5 animate-spin text-[#C5A059]" /></div>
            ) : (
              <>
                {okData.ok_confirmed && (
                  <p className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-2" data-testid="ok-link-confirmed-note">✅ Este cliente já deu o OK anteriormente — o link mostrará "plano ativo".</p>
                )}
                <p className="text-xs text-slate-500">O cliente toca, confere o plano e dá o OK — o boleto D+3 sai na hora e chega no WhatsApp dele.</p>
                <p className="text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 break-all" data-testid="ok-link-url">{okData.ok_url}</p>
                <div className="flex flex-col gap-2">
                  <Button onClick={sendOkWhatsapp} disabled={okSending || !okData.whatsapp}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="ok-link-send-btn">
                    {okSending ? <LoaderIcon className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {okData.whatsapp ? `Enviar no WhatsApp do cliente (${okData.whatsapp})` : 'Cliente sem WhatsApp cadastrado'}
                  </Button>
                  <Button variant="outline" className="w-full gap-2" data-testid="ok-link-copy-btn"
                    onClick={() => { navigator.clipboard.writeText(okData.ok_url); toast.success('Link OK copiado'); }}>
                    <Copy className="w-4 h-4" /> Copiar link (enviar eu mesmo)
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Info Modal */}
      {priceCompany && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4" data-testid="custom-price-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#0A2647] flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#C5A059]" /> Preço Personalizado</h3>
                <p className="text-xs text-slate-500 mt-0.5">{priceCompany.name} · {PLAN_LABELS[priceCompany.plan] || priceCompany.plan}</p>
              </div>
              <button onClick={() => setPriceCompany(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between text-sm">
              <span className="text-slate-500">Preço cheio do plano</span>
              <span className="font-bold text-[#0A2647]">R$ {fullPriceOf(priceCompany).toFixed(2).replace('.', ',')}/mês</span>
            </div>
            {priceCompany.custom_price != null && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between text-sm">
                <span className="text-amber-800">Preço atual (personalizado)</span>
                <span className="font-bold text-[#C5A059]">R$ {priceCompany.custom_price.toFixed(2).replace('.', ',')}/mês</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Novo valor (R$)</label>
                <input value={priceValue} onChange={e => { setPriceValue(e.target.value); setPricePct(''); }}
                  placeholder="Ex: 45,00" inputMode="decimal" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="custom-price-value" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">ou desconto (%)</label>
                <input value={pricePct} onChange={e => { setPricePct(e.target.value); setPriceValue(''); }}
                  placeholder="Ex: 25" inputMode="decimal" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="custom-price-pct" />
              </div>
            </div>
            {(() => {
              const full = fullPriceOf(priceCompany);
              const v = parseFloat((priceValue || '').replace(',', '.'));
              const p = parseFloat((pricePct || '').replace(',', '.'));
              const preview = v > 0 ? v : (p > 0 ? full * (1 - p / 100) : null);
              return preview != null && full > 0 ? (
                <p className="text-xs text-slate-500" data-testid="custom-price-preview">
                  Cliente vai pagar <b className="text-[#0A2647]">R$ {preview.toFixed(2).replace('.', ',')}/mês</b>
                  {preview < full && <span className="text-[#C5A059] font-semibold"> (−{((1 - preview / full) * 100).toFixed(0)}%)</span>}
                  {preview > full && <span className="text-rose-600 font-semibold"> (acima do preço cheio)</span>}
                </p>
              ) : null;
            })()}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Motivo (auditoria)</label>
              <input value={priceReason} onChange={e => setPriceReason(e.target.value)}
                placeholder="Ex: negociação grupo 3 lojas" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="custom-price-reason" />
            </div>
            <p className="text-[11px] text-slate-400">A assinatura no Asaas é atualizada na hora — a <b>próxima fatura</b> já vem com o novo valor. Faturas já emitidas não mudam.</p>
            <div className="flex gap-2">
              <Button onClick={handleSavePrice} disabled={savingPrice} className="flex-1 bg-[#0A2647] hover:bg-[#0e3260] text-white" data-testid="custom-price-save">
                {savingPrice ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar novo preço'}
              </Button>
              {priceCompany.custom_price != null && (
                <Button variant="outline" disabled={savingPrice} data-testid="custom-price-reset"
                  onClick={() => window.confirm('Restaurar o preço cheio do plano para este cliente?') && submitCustomPrice({ reset: true })}>
                  Restaurar preço cheio
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {editInfoCompany && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4" data-testid="edit-info-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold text-[#0A2647] mb-4">Editar: {editInfoCompany.name}</h3>
            <div className="space-y-4">
              <div><label className="text-xs font-medium text-slate-600 mb-1 block">Nome da Empresa</label><input value={editInfoData.company_name} onChange={e => setEditInfoData({...editInfoData, company_name: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-company-name" /></div>
              <div><label className="text-xs font-medium text-slate-600 mb-1 block">Nome do Dono</label><input value={editInfoData.owner_name} onChange={e => setEditInfoData({...editInfoData, owner_name: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-owner-name" /></div>
              <div><label className="text-xs font-medium text-slate-600 mb-1 block">Email do Dono</label><input value={editInfoData.owner_email} onChange={e => setEditInfoData({...editInfoData, owner_email: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-owner-email" /></div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">CPF/CNPJ</label>
                <input
                  value={editInfoData.document || ''}
                  onChange={e => setEditInfoData({ ...editInfoData, document: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                  placeholder="Só números (11 = CPF · 14 = CNPJ)"
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${editInfoData.document && ![11, 14].includes(editInfoData.document.length) ? 'border-red-400 bg-red-50' : ''}`}
                  data-testid="edit-company-document"
                />
                <p className="text-[10px] text-slate-500 mt-1">Obrigatório para colocar a empresa em plano pago — é o documento usado na cobrança do Asaas.</p>
              </div>
              <div><label className="text-xs font-medium text-slate-600 mb-1 block">WhatsApp do Dono</label><input value={editInfoData.owner_whatsapp} onChange={e => setEditInfoData({...editInfoData, owner_whatsapp: e.target.value})} placeholder="DDD + número" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-owner-whatsapp" /></div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Link da empresa</label>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-400">falocomodono.com.br/</span>
                  <input value={editInfoData.slug} onChange={e => setEditInfoData({...editInfoData, slug: e.target.value})} className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="edit-company-slug" />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Ao trocar, o link antigo continua funcionando (redireciona) — placas já impressas não quebram. Baixe a placa nova depois de salvar.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Plano</label>
                <div className="grid grid-cols-2 gap-2">{PLAN_OPTIONS.map(p => (
                  <button key={p.id} onClick={() => setEditInfoData({...editInfoData, plan: p.id})} className={`p-3 rounded-xl border-2 text-left transition-all ${editInfoData.plan === p.id ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`plan-option-${p.id}`}><p className="font-bold text-sm text-slate-900">{p.label}</p><p className="text-[10px] text-slate-500">{p.desc}</p></button>
                ))}</div>
              </div>
              {editInfoData.plan && !['free', 'livre'].includes(editInfoData.plan) && editInfoCompany.plan === 'free' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="first-due-block">
                  <label className="text-xs font-semibold text-amber-800 mb-1 block">1º vencimento da mensalidade</label>
                  <input
                    type="date"
                    value={editInfoData.first_due_date || ''}
                    onChange={e => setEditInfoData({ ...editInfoData, first_due_date: e.target.value })}
                    className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="edit-first-due-date"
                  />
                  <p className="text-[10px] text-amber-700 mt-1">
                    Sugerido: cadastro + 30 dias (1º mês cortesia). Se essa data já passou, usamos hoje + 3 dias.
                    Sem CPF/CNPJ na empresa, nenhuma cobrança é criada no Asaas.
                  </p>
                </div>
              )}

              {infoError && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3" data-testid="edit-info-error">
                  <p className="text-xs font-bold text-red-700 mb-1">Atenção</p>
                  <p className="text-xs text-red-700 leading-relaxed">{infoError}</p>
                </div>
              )}

              <div><label className="text-xs font-medium text-slate-600 mb-1 block">Bonus valido ate (opcional)</label><input type="date" value={editInfoData.vip_expires_at} onChange={e => setEditInfoData({...editInfoData, vip_expires_at: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-vip-expires" /></div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Afiliado Responsavel</label>
                <select
                  value={editInfoData.referred_by}
                  onChange={e => setEditInfoData({...editInfoData, referred_by: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  data-testid="edit-referred-by"
                >
                  <option value="">-- Organico (sem afiliado) --</option>
                  {affiliates.map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.email || a.id.slice(0,8)})</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">Vincule a empresa a um afiliado para ele receber a comissao recorrente dessa assinatura.</p>
              </div>

              {/* WhatsApp Test */}
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div><p className="text-xs font-semibold text-emerald-700 flex items-center gap-1"><Phone className="w-3 h-3" />Testar WhatsApp</p><p className="text-[10px] text-emerald-600 mt-0.5">Envia mensagem de teste para os números cadastrados</p></div>
                  <Button onClick={() => testWhatsApp(editInfoCompany.id)} disabled={testingWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-3" data-testid="test-whatsapp-btn">{testingWhatsApp ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Enviar Teste'}</Button>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={saveEditInfo} disabled={savingInfo} className="flex-1 bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-semibold" data-testid="save-edit-info">{savingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Alteracoes'}</Button>
              <Button variant="outline" onClick={() => setEditInfoCompany(null)} className="flex-1">Cancelar</Button>
            </div>
          </div>
        </div>
      )}

      {/* ═════ Multi-User Manager Modal ═════ */}
      {usersCompany && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4" data-testid="users-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[88vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-[#0A2647]">Usuarios da empresa</h3>
                <p className="text-xs text-slate-500 mt-0.5">{usersCompany.name}</p>
              </div>
              <button onClick={() => { setUsersCompany(null); setResetUserId(null); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400" data-testid="close-users-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Lista de usuarios */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Usuarios com acesso ({usersList.length})</p>
                {usersLoading ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#C5A059]" /></div>
                ) : usersList.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Nenhum usuario encontrado.</p>
                ) : (
                  <div className="space-y-2">
                    {usersList.map(u => (
                      <div key={u.id} className="border border-slate-200 rounded-xl p-3 bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-slate-800 truncate">{u.name}</p>
                            <p className="text-xs text-slate-500 truncate">{u.email}</p>
                            {u.created_by_admin && <span className="inline-block mt-1 text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">CRIADO PELO ADMIN</span>}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button onClick={() => { setResetUserId(u.id); setResetPassword(''); }} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Resetar senha" data-testid={`reset-pw-${u.id}`}><KeyRound className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleRemoveUser(u.id, u.email)} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors" title="Remover acesso" data-testid={`remove-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                        {resetUserId === u.id && (
                          <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-2">
                            <input
                              type="text"
                              value={resetPassword}
                              onChange={(e) => setResetPassword(e.target.value)}
                              placeholder="Nova senha (min. 6 caracteres)"
                              className="flex-1 border rounded-lg px-3 py-1.5 text-sm"
                              data-testid={`new-pw-input-${u.id}`}
                            />
                            <Button onClick={handleResetPassword} disabled={resetting} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-3" data-testid={`confirm-reset-${u.id}`}>
                              {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Salvar'}
                            </Button>
                            <button onClick={() => setResetUserId(null)} className="text-xs text-slate-400 hover:text-slate-600">Cancelar</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Adicionar novo usuario */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-3 flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" />Adicionar Novo Usuario</p>
                <div className="space-y-2.5">
                  <input
                    value={newUserForm.name}
                    onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                    placeholder="Nome do usuario"
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="new-user-name"
                  />
                  <input
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                    placeholder="email@empresa.com"
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="new-user-email"
                  />
                  <input
                    type="text"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                    placeholder="Senha temporaria (min. 6 caracteres)"
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="new-user-password"
                  />
                  <Button
                    onClick={handleAddUser}
                    disabled={savingNewUser}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                    data-testid="save-new-user"
                  >
                    {savingNewUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    Adicionar usuario
                  </Button>
                  <p className="text-[10px] text-emerald-700 leading-relaxed">
                    💡 Compartilhe a senha temporaria com o usuario. Ele pode trocar depois em &quot;Esqueci minha senha&quot;.
                    Se o email ja existir em outro usuario, ele apenas ganha acesso a esta empresa adicional.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Partner Modal */}
      {linkPartnerCompany && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4" data-testid="link-partner-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-bold text-cyan-600 uppercase tracking-wider mb-1">Vincular Parceiro</p>
                <h3 className="text-lg font-bold text-[#0A2647]">{linkPartnerCompany.name}</h3>
                <p className="text-xs text-slate-500 mt-0.5">/{linkPartnerCompany.slug}</p>
              </div>
              <button onClick={() => setLinkPartnerCompany(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-slate-600 leading-relaxed">
                Use isso para corrigir empresas que se cadastraram <strong>antes</strong> da correção do cookie ref_partner. Após vincular, ela passa a aparecer no painel daquele parceiro e gera comissão a partir do próximo pagamento.
              </p>
            </div>

            <label className="text-xs font-medium text-slate-600 mb-1 block">Parceiro</label>
            <select
              value={selectedPartnerId}
              onChange={(e) => setSelectedPartnerId(e.target.value)}
              className="w-full h-11 border border-slate-300 rounded-lg px-3 text-sm bg-white"
              data-testid="link-partner-select"
            >
              <option value="">— Sem parceiro (orgânico) —</option>
              {partnersList.map(p => (
                <option key={p.id} value={p.id}>{p.name} (/{p.slug})</option>
              ))}
            </select>

            <div className="flex gap-2 mt-5">
              <Button variant="outline" onClick={() => setLinkPartnerCompany(null)} className="flex-1">
                Cancelar
              </Button>
              <Button
                onClick={handleSaveLinkPartner}
                disabled={savingLinkPartner}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="link-partner-save"
              >
                {savingLinkPartner ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompaniesTab;
